var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Autocomplete, Box, Checkbox, Chip, CircularProgress, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, IconButton, InputLabel, MenuItem, OutlinedInput, Radio, RadioGroup, Select, TextField, Tooltip, Typography, } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Calendar, ClockCountdown, ImageSquare, Info, X } from "@phosphor-icons/react";
import dayjs from "dayjs";
import React, { Fragment, useContext, useCallback, useEffect, useRef, useState, useMemo, } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import LeadsObjectApi from "../../../infrastructure/api/pages/lead-object-api";
import ObjectStatusApi from "../../../infrastructure/api/pages/object-status-api";
import OriginApi from "../../../infrastructure/api/pages/origin-api";
import { UserRolesPermissionsContext } from "../../../context/UserRolesPermissionsContext";
import { AuthenticationContext } from "../../../context/AuthenticationContext";
import ContractAffectedConfirmationDialog from "../../components/ContractAffectedConfirmationDialog";
import { checkCreateEdit, hasOnlyReadPermission, hasPartialAccess } from "../../../application/common/utils/roles-helper";
import { Modules } from "../../../application/common/enums/screen-name";
import ZoomableImage from "../../components/LeadDetail/LeadObject/Image";
import CustomModal from "../modal/CustomModal";
import "./ObjectModal.scss";
const ObjectModal = ({ open, onClose, refresh, initialFormData, data, assetsData, setActionTabRefresh, companyRoles, statusTree, leadId: leadIdProp, companyId: companyIdProp, readOnly, }) => {
    var _a, _b, _c, _d, _e;
    const [saving, setSaving] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState(data.imageUrl);
    const [selectedImage, setSelectedImage] = useState(data.image);
    // Selecteerd assets panel: collapsed to a few rows + "+N" until the field is
    // focused, then expands to show every selected asset for editing.
    const [assetsPanelFocused, setAssetsPanelFocused] = useState(false);
    const ASSETS_COLLAPSED_COUNT = 3;
    const hiddenButtonRef = useRef(null);
    const selectionInputRef = useRef(null);
    const internalCommentsRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [objectId, setObjectId] = useState("");
    const [showValidation, setShowValidation] = useState(false);
    // Dynamic status selections (statusTree is now passed as prop)
    const [level1Options, setLevel1Options] = useState([]);
    const [level2Options, setLevel2Options] = useState([]);
    const [level3Options, setLevel3Options] = useState([]);
    const [statusLevel1Id, setStatusLevel1Id] = useState("");
    const [statusLevel2Id, setStatusLevel2Id] = useState("");
    const [statusLevel3Id, setStatusLevel3Id] = useState("");
    // Track original status values to detect changes
    const [originalStatusLevel1Id, setOriginalStatusLevel1Id] = useState("");
    const [originalStatusLevel2Id, setOriginalStatusLevel2Id] = useState("");
    const [originalStatusLevel3Id, setOriginalStatusLevel3Id] = useState("");
    // Contract affected confirmation dialog states
    const [showContractConfirmation, setShowContractConfirmation] = useState(false);
    const [affectedContracts, setAffectedContracts] = useState([]);
    const [checkingContracts, setCheckingContracts] = useState(false);
    const [pendingFormData, setPendingFormData] = useState(null);
    const debounceTimer = useRef(undefined); // To store the timeout ID for debouncing
    const [userPreferredMonth, setUserPreferredMonth] = React.useState((_a = data.userPreferredMonth) !== null && _a !== void 0 ? _a : false);
    const [locations, setLocations] = useState([]);
    const [origins, setOrigins] = useState([]);
    const { t: translate } = useTranslation();
    const { RolesPermissions } = useContext(UserRolesPermissionsContext);
    const { initials } = useContext(AuthenticationContext);
    // Check user permission levels for changing object status
    const isReadOnly = hasOnlyReadPermission(RolesPermissions, Modules.ChangeObjectStatus);
    const isPartialAccess = hasPartialAccess(RolesPermissions, Modules.ChangeObjectStatus);
    const canChangeObjectStatus = checkCreateEdit(RolesPermissions, Modules.ChangeObjectStatus);
    // Define which statuses are RESTRICTED for partial access (blacklist approach)
    // Partial access CANNOT work with: Signed (Ondertekend), the automatic Compleet,
    // or the manual Afgerond richting planning
    const partialAccessRestrictedStatuses = useMemo(() => ["Ondertekend", "Compleet", "Afgerond richting planning", "Signed", "Completed"], []);
    // Determine if status dropdowns should be disabled
    // Disabled when: User has only read permission
    const isStatusDropdownDisabled = useMemo(() => {
        if (isReadOnly)
            return true; // Read-only users can never edit
        return false; // Full access and partial access (when shown) can edit
    }, [isReadOnly]);
    // Determine if status dropdowns should be completely hidden
    // Hidden when: 1) No permission at all OR 2) Partial access AND object status is Signed/Completed
    const shouldHideStatusDropdowns = useMemo(() => {
        if (!canChangeObjectStatus)
            return true; // No permission at all - hide completely
        if (isPartialAccess) {
            // Partial access: hide if object status is restricted (Signed/Completed)
            // If status is NOT restricted, show with editable functionality
            const currentStatus = data.statusLevel1Name || "";
            return partialAccessRestrictedStatuses.includes(currentStatus);
        }
        return false; // Full access and read-only always show
    }, [canChangeObjectStatus, isPartialAccess, data.statusLevel1Name, partialAccessRestrictedStatuses]);
    const { reset, handleSubmit, control, register, getValues, setValue, resetField, clearErrors, setError, formState: { errors }, } = useForm();
    // Item #1: multiplicativeFactor no longer has an editable control in this modal
    // (hidden per client request) — kept as passthrough state only, resynced from
    // `data.multiplicativeFactorEnabled` on load and written back unchanged on submit.
    const [multiplicativeFactor, setMultiplicativeFactor] = React.useState((_b = data.multiplicativeFactorEnabled) !== null && _b !== void 0 ? _b : false);
    const handleUserPreferredCheckBoxChange = (event) => {
        setUserPreferredMonth(event.target.checked === true);
    };
    const [drawingAvailable, setdrawingAvailable] = React.useState((_c = data.drawingNotAvailable) !== null && _c !== void 0 ? _c : false);
    const [syncedDrawingUrls, setSyncedDrawingUrls] = useState([]);
    const [syncedDrawingNames, setSyncedDrawingNames] = useState([]);
    const [additionalDrawingLink, setAdditionalDrawingLink] = useState('');
    // If editing and object is contracted, we bypass client-side validations
    const isEditingContracted = Boolean((data === null || data === void 0 ? void 0 : data.id) && (data === null || data === void 0 ? void 0 : data.isContracted));
    const handleDrawingRadioChange = (event) => {
        setdrawingAvailable(event.target.value === "true");
    };
    // Item #1: "Interne objectopmerkingen" timestamp quick-insert (button + F10).
    // Format is fixed by the client: "INITIALS | DD-MM-YYYY HH:mm:" using the
    // logged-in user's stored Initials (never derived from their name here) and
    // the client machine's local time.
    const insertTimestamp = useCallback(() => {
        var _a, _b, _c;
        if (!initials) {
            toast.warning(translate("Stel eerst uw initialen in via het gebruikersmenu voordat u een tijdstempel kunt invoegen."));
            return;
        }
        const now = new Date();
        const pad = (n) => n.toString().padStart(2, "0");
        const stamp = `${initials} | ${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}:`;
        const textarea = internalCommentsRef.current;
        const currentValue = (_a = getValues("internalComments")) !== null && _a !== void 0 ? _a : "";
        const isFocused = !!textarea && document.activeElement === textarea;
        let newValue;
        let newCursorPos;
        if (isFocused && textarea) {
            const start = (_b = textarea.selectionStart) !== null && _b !== void 0 ? _b : currentValue.length;
            const end = (_c = textarea.selectionEnd) !== null && _c !== void 0 ? _c : currentValue.length;
            const before = currentValue.slice(0, start);
            const after = currentValue.slice(end);
            const atLineStart = start === 0 || before.endsWith("\n");
            const insertText = atLineStart ? stamp : `\n${stamp}`;
            newValue = before + insertText + after;
            newCursorPos = before.length + insertText.length;
        }
        else if (currentValue.length === 0) {
            newValue = stamp;
            newCursorPos = newValue.length;
        }
        else if (currentValue.endsWith("\n")) {
            newValue = currentValue + stamp;
            newCursorPos = newValue.length;
        }
        else {
            newValue = currentValue + "\n" + stamp;
            newCursorPos = newValue.length;
        }
        setValue("internalComments", newValue, { shouldDirty: true, shouldTouch: true });
        requestAnimationFrame(() => {
            const el = internalCommentsRef.current;
            if (el) {
                el.focus();
                el.setSelectionRange(newCursorPos, newCursorPos);
            }
        });
    }, [initials, getValues, setValue, translate]);
    // F10 inserts a timestamp anywhere in this modal, not just while the
    // textarea has focus. Listener is scoped to the modal's open lifecycle so
    // it never fires once closed and never doubles up on reopen.
    useEffect(() => {
        if (!open)
            return;
        const handleKeyDown = (event) => {
            if (event.key === "F10") {
                event.preventDefault();
                insertTimestamp();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [open, insertTimestamp]);
    const handleSaveButtonClick = () => {
        // Show validation errors
        setShowValidation(true);
        if (isEditingContracted) {
            // Directly submit current form values without triggering validations
            const currentValues = getValues();
            onSubmit(currentValues);
            return;
        }
        if (hiddenButtonRef.current) {
            hiddenButtonRef.current.click();
        }
    };
    const onSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const urlParams = new URLSearchParams(window.location.search);
        const leadId = leadIdProp !== null && leadIdProp !== void 0 ? leadIdProp : urlParams.get("id");
        const companyId = companyIdProp !== null && companyIdProp !== void 0 ? companyIdProp : urlParams.get("companyId");
        // Validate dependent dropdown selections (only if status Level 1 is selected)
        if (statusLevel1Id) {
            if (level2Options.length > 0 && !statusLevel2Id) {
                toast.warning(translate("selectReason"));
                return;
            }
            if (statusLevel2Id && level3Options.length > 0 && !statusLevel3Id) {
                toast.warning(translate("selectFromDropdown"));
                return;
            }
        }
        if (leadId && companyId) {
            data.openingEndHours =
                data.openingEndHoursString && data.openingEndHoursString !== ""
                    ? data.openingEndHoursString + ":00"
                    : null;
            data.openingStartHours =
                data.openingStartHoursString && data.openingStartHoursString !== ""
                    ? data.openingStartHoursString + ":00"
                    : null;
            data.companyId = companyId;
            data.leadId = leadId;
            data.image = selectedImage ? selectedImage : data.image;
            data.imageUrl = selectedImageUrl ? selectedImageUrl : data.imageUrl;
            data.multiplicativeFactorEnabled = multiplicativeFactor;
            data.userPreferredMonth = userPreferredMonth;
            data.drawingNotAvailable = drawingAvailable;
            const pathIds = [statusLevel1Id, statusLevel2Id, statusLevel3Id].filter(Boolean);
            const originalPathIds = [originalStatusLevel1Id, originalStatusLevel2Id, originalStatusLevel3Id].filter(Boolean);
            // Check if status has actually changed
            const hasStatusChanged = () => {
                if (pathIds.length !== originalPathIds.length)
                    return true;
                if (statusLevel1Id !== originalStatusLevel1Id)
                    return true;
                if (statusLevel2Id !== originalStatusLevel2Id)
                    return true;
                if (statusLevel3Id !== originalStatusLevel3Id)
                    return true;
                return false;
            };
            // Check if we need to show confirmation dialog
            // Only check when editing existing object AND status has changed
            if (data.id && hasStatusChanged() && pathIds.length > 0) {
                try {
                    setCheckingContracts(true);
                    const response = yield LeadsObjectApi.checkAffectedContracts({
                        leadId,
                        objectIds: [data.id],
                        statusPathIds: pathIds,
                    });
                    if (response.data.hasAffectedContracts) {
                        // Store form data and show confirmation dialog
                        setPendingFormData(data);
                        setAffectedContracts(response.data.affectedContracts);
                        setShowContractConfirmation(true);
                        setCheckingContracts(false);
                        return; // Wait for user confirmation
                    }
                }
                catch (e) {
                    console.error("Error checking affected contracts:", e);
                    toast.error(((_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.data) || translate("Error checking affected contracts"));
                    setCheckingContracts(false);
                    return;
                }
                finally {
                    setCheckingContracts(false);
                }
            }
            // Proceed with save (either no confirmation needed or user confirmed)
            yield performSave(data, pathIds, hasStatusChanged(), leadId);
        }
    });
    // Extracted save logic for reuse after confirmation
    const performSave = (data, pathIds, statusChanged, leadId) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        const persistSelections = (objectGuid) => __awaiter(void 0, void 0, void 0, function* () {
            // Only call API if status actually changed and pathIds is not empty
            if (pathIds.length > 0 && statusChanged) {
                yield ObjectStatusApi.upsertSelections(objectGuid, pathIds, leadId !== null && leadId !== void 0 ? leadId : undefined);
            }
        });
        // Write back synced drawing URLs and names with additional link
        if (data.referralObjectNumber != null) {
            data.drawingLink = syncedDrawingUrls.join(',');
            const namesStr = syncedDrawingNames.join(',');
            data.drawingLinkNames = additionalDrawingLink.trim()
                ? `${namesStr}|${additionalDrawingLink.trim()}`
                : namesStr;
        }
        // Ensure objectFullAddress is set. It is only populated when the user actively
        // picks a PDOK Locatie option; otherwise rebuild it from the address parts in the
        // same PDOK weergavenaam format: "{street} {houseNumber}{addition}, {postalCode} {city}".
        if (!data.objectFullAddress || !data.objectFullAddress.trim()) {
            const number = `${(_a = data.houseNumber) !== null && _a !== void 0 ? _a : ""}${(_b = data.addition) !== null && _b !== void 0 ? _b : ""}`.trim();
            const streetPart = [data.street, number].filter(Boolean).join(" ").trim();
            const cityPart = [data.postalCode, data.city].filter(Boolean).join(" ").trim();
            const rebuilt = [streetPart, cityPart].filter(Boolean).join(", ");
            data.objectFullAddress = rebuilt || undefined;
        }
        if (!data.id) {
            // Creating new object
            setSaving(true);
            try {
                const response = yield LeadsObjectApi.addLeadObject(data);
                if (response.data && response.status === 200) {
                    toast.success(translate("SavedSuccessfully"));
                    const createdId = ((_c = response.data) === null || _c === void 0 ? void 0 : _c.id) || data.id;
                    if (createdId) {
                        try {
                            yield persistSelections(createdId);
                        }
                        catch (error) {
                            console.error("Error persisting selections:", error);
                        }
                    }
                    setSaving(false);
                    onClose();
                    refresh();
                    setActionTabRefresh((prev) => !prev);
                }
                else {
                    setSaving(false);
                }
            }
            catch (error) {
                console.error("Error creating object:", error);
                setSaving(false);
            }
        }
        else {
            // Updating existing object
            setSaving(true);
            try {
                const response = yield LeadsObjectApi.updateLeadObject(data);
                if (response.status === 201) {
                    // No changes made to object details
                    // Check if status has changed - if yes, proceed with status update
                    if (statusChanged && pathIds.length > 0) {
                        try {
                            yield persistSelections(data.id);
                            toast.success(translate("UpdatedSuccessfully"));
                            setSaving(false);
                            onClose();
                            refresh();
                            setActionTabRefresh((prev) => !prev);
                        }
                        catch (error) {
                            console.error("Error persisting selections:", error);
                            setSaving(false);
                        }
                    }
                    else {
                        // No changes at all
                        toast.warning(translate("NoChangesMade"));
                        setSaving(false);
                    }
                }
                else if (response.data && response.status === 200) {
                    toast.success(translate("UpdatedSuccessfully"));
                    try {
                        yield persistSelections(data.id);
                    }
                    catch (error) {
                        console.error("Error persisting selections:", error);
                    }
                    setSaving(false);
                    onClose();
                    refresh();
                    setActionTabRefresh((prev) => !prev);
                }
                else {
                    setSaving(false);
                }
            }
            catch (error) {
                console.error("Error updating object:", error);
                setSaving(false);
            }
        }
    });
    // Handle confirmation dialog accept
    const handleConfirmStatusChange = () => __awaiter(void 0, void 0, void 0, function* () {
        setShowContractConfirmation(false);
        if (pendingFormData) {
            const urlParams = new URLSearchParams(window.location.search);
            const leadId = leadIdProp !== null && leadIdProp !== void 0 ? leadIdProp : urlParams.get("id");
            const pathIds = [statusLevel1Id, statusLevel2Id, statusLevel3Id].filter(Boolean);
            yield performSave(pendingFormData, pathIds, true, leadId);
            setPendingFormData(null);
        }
    });
    // Handle confirmation dialog cancel
    const handleCancelStatusChange = () => {
        setShowContractConfirmation(false);
        setAffectedContracts([]);
        setPendingFormData(null);
    };
    const handleButtonClick = () => __awaiter(void 0, void 0, void 0, function* () {
        const formData = getValues(); // Access form data
        try {
            yield fetchImage(formData.longitude, formData.latitude);
        }
        catch (error) {
            toast.error(translate(String(error)));
            setLoading(false);
            setIsDisabled(false);
        }
    });
    const fetchImage = (longitude, latitude) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            // Validate coordinates
            if (!validateCoordinates(longitude, latitude)) {
                toast.error(translate("InvalidCoordinates"));
                return;
            }
            setLoading(true);
            setIsDisabled(true);
            setSelectedImageUrl(null);
            setSelectedImage(null);
            const query = {
                objectId: objectId,
                longitude,
                latitude,
            };
            LeadsObjectApi.fetchObjectImage(query).then((response) => {
                if (response.status === 200) {
                    const imageUrl = response.data.imageUrl;
                    if (imageUrl) {
                        setLoading(false);
                        setIsDisabled(false);
                        setSelectedImageUrl(imageUrl);
                        setSelectedImage(response.data.imageName);
                    }
                    else {
                        toast.error(response.data.errorMessage || translate("FailedToLoadImage"));
                        setLoading(false);
                        setIsDisabled(false);
                        setSelectedImageUrl(null);
                        setSelectedImage(null);
                    }
                }
            });
        }
        catch (error) {
            console.error("Error fetching image:", error);
            toast.error(translate("ErrorFetchingImage"));
            setLoading(false);
            setIsDisabled(false);
            setSelectedImageUrl(null);
            setSelectedImage(null);
        }
    });
    const validateCoordinates = (longitude, latitude) => {
        const lat = parseFloat(latitude);
        const lon = parseFloat(longitude);
        return (!isNaN(lat) &&
            !isNaN(lon) &&
            lat >= -90 &&
            lat <= 90 &&
            lon >= -180 &&
            lon <= 180);
    };
    const resetLocation = () => {
        resetField("latitude");
        resetField("longitude");
        setValue("street", "");
        setValue("houseNumber", "");
        setValue("addition", "");
        setValue("locationId", "");
        setValue("postalCode", "");
        setValue("city", "");
        setValue("locationId", "");
        setValue("location", null);
    };
    const searchLocation = (query) => {
        if (query) {
            LeadsObjectApi.searchLocation(query).then((response) => {
                if (response.status === 200) {
                    const mappedLocations = response.data.response.docs.map((location) => ({
                        id: location === null || location === void 0 ? void 0 : location.id,
                        weergavenaam: location === null || location === void 0 ? void 0 : location.weergavenaam,
                        score: location === null || location === void 0 ? void 0 : location.score,
                        isFakeId: location === null || location === void 0 ? void 0 : location.isFakeId,
                    }));
                    setLocations(mappedLocations);
                    setIsFetching(false);
                }
            });
        }
        setIsFetching(false);
    };
    const getLocationById = (id) => {
        LeadsObjectApi.getLocationById(id).then((response) => {
            var _a, _b, _c, _d, _e, _f;
            if (response.status === 200) {
                const location = response.data.response.docs[0];
                const coordinates = location.centroide_ll.match(/POINT\(([^ ]+) ([^)]+)\)/);
                setValue("street", (_a = location === null || location === void 0 ? void 0 : location.straatnaam) !== null && _a !== void 0 ? _a : "");
                setValue("houseNumber", (_c = (_b = location === null || location === void 0 ? void 0 : location.huisnummer) === null || _b === void 0 ? void 0 : _b.toString()) !== null && _c !== void 0 ? _c : "");
                setValue("addition", `${(_d = location === null || location === void 0 ? void 0 : location.huisletter) !== null && _d !== void 0 ? _d : ""}${(location === null || location === void 0 ? void 0 : location.huisnummertoevoeging)
                    ? "-" + location.huisnummertoevoeging
                    : ""}`);
                setValue("locationId", location === null || location === void 0 ? void 0 : location.id);
                setValue("postalCode", (_e = location === null || location === void 0 ? void 0 : location.postcode) !== null && _e !== void 0 ? _e : "");
                setValue("city", (_f = location.woonplaatsnaam) !== null && _f !== void 0 ? _f : "");
                if (location.adresseerbaarobject_id != null &&
                    location.adresseerbaarobject_id !== undefined &&
                    location.adresseerbaarobject_id !== "") {
                    LeadsObjectApi.getPandIdByAddressObjectId(location.adresseerbaarobject_id).then((response) => {
                        var _a;
                        if (response.status === 200 && response.data) {
                            const pandIds = response.data || [];
                            if (pandIds.length > 0) {
                                setValue("pandId", (_a = pandIds[0]) !== null && _a !== void 0 ? _a : "");
                            }
                        }
                    }).catch((error) => {
                        console.error("Error fetching PandIds:", error);
                    });
                }
                if (coordinates) {
                    setValue("longitude", coordinates[1]);
                    setValue("latitude", coordinates[2]);
                    handleButtonClick();
                }
            }
        });
    };
    const fetchLocations = (query) => {
        setIsFetching(true);
        searchLocation(query);
    };
    const onLocationChange = (value) => {
        if (value) {
            if (!value.isFakeId) {
                setValue("objectFullAddress", value.weergavenaam || undefined);
                getLocationById(value.id);
            }
        }
        else {
            resetLocation();
            setValue("objectFullAddress", undefined);
            setSelectedImageUrl(null);
            setSelectedImage(null);
        }
    };
    // Function to find the text based on the ID
    const findTextFromId = React.useCallback((ID) => {
        const option = assetsData.find((option) => option.value === ID);
        return option ? option.text : "No Record Found";
    }, [assetsData]);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f;
        setValue("preferredMonth", data.preferredMonth);
        if (data.objectID) {
            setObjectId(data.objectID.toString());
        }
        if (data.locationId || data.street || data.city || data.objectFullAddress) {
            const weergavenaam = data.objectFullAddress || [
                data.street,
                data.houseNumber,
                data.addition,
                data.postalCode,
                data.city,
            ]
                .filter(Boolean)
                .join(" ")
                .replace(/\s+/g, " ")
                .trim();
            const location = {
                id: data.locationId || "display-only",
                weergavenaam: weergavenaam || data.locationId || "",
                isFakeId: !data.locationId,
            };
            data.location = location;
            // Pre-populate options so autocomplete can render the value
            setLocations([location]);
            if (((_a = data.selectionListSystemsId) === null || _a === void 0 ? void 0 : _a.length) !== undefined &&
                ((_b = data.selectionListSystemsId) === null || _b === void 0 ? void 0 : _b.length) > 0) {
                const savedIdsFromDatabase = data.selectionListSystemsId;
                data.selectionListSystems = savedIdsFromDatabase.map((id) => ({
                    value: id,
                    text: findTextFromId(id),
                }));
            }
        }
        // Map origins if available
        if (((_c = data.originIds) === null || _c === void 0 ? void 0 : _c.length) && origins.length > 0) {
            data.origins = origins.filter((origin) => {
                return typeof origin.id === 'string' && data.originIds.includes(origin.id);
            });
        }
        // Parse RoofAccess from context field if roofAccess not already set
        if (!data.roofAccess && data.context) {
            const roofAccessMap = {
                "mobiele ladder < 6m": 1,
                "mobiele ladder > 6m": 2,
                "kooi of gevel ladder": 3,
                "dakluik": 4,
                "deur": 5,
                "raam uitstap": 6,
                "anders": 7,
                "hoogwerker": 8,
                "onbekend": 9,
            };
            const roofAccessMatch = data.context.match(/RoofAccess:\s*(.+)/i);
            if (roofAccessMatch) {
                const roofAccessText = roofAccessMatch[1].trim().toLowerCase();
                const mappedValue = roofAccessMap[roofAccessText];
                if (mappedValue) {
                    data.roofAccess = mappedValue;
                }
                // Strip RoofAccess line from context
                data.context = data.context.replace(/RoofAccess:\s*.+(\n|$)/i, "").trim();
            }
        }
        reset(data);
        setMultiplicativeFactor((_d = data.multiplicativeFactorEnabled) !== null && _d !== void 0 ? _d : false);
        setdrawingAvailable((_e = data.drawingNotAvailable) !== null && _e !== void 0 ? _e : false);
        setUserPreferredMonth((_f = data.userPreferredMonth) !== null && _f !== void 0 ? _f : false);
        // Load synced drawing URLs from backend (SAS URLs generated at sync time)
        if (data.drawingLinks && data.drawingLinks.length > 0) {
            setSyncedDrawingUrls(data.drawingLinks);
        }
        else if (data.drawingLink) {
            setSyncedDrawingUrls(data.drawingLink.split(',').map((u) => u.trim()).filter(Boolean));
        }
        else {
            setSyncedDrawingUrls([]);
        }
        // Load drawing link display names and additional text
        if (data.drawingLinkNames) {
            const [namesStr, extraLink] = data.drawingLinkNames.split('|');
            setSyncedDrawingNames((namesStr || '').split(',').map((n) => n.trim()).filter(Boolean));
            setAdditionalDrawingLink((extraLink === null || extraLink === void 0 ? void 0 : extraLink.trim()) || '');
        }
        else {
            setSyncedDrawingNames([]);
            setAdditionalDrawingLink('');
        }
    }, [data, reset, setValue, findTextFromId, origins]);
    // Load origins when modal opens
    useEffect(() => {
        if (open) {
            OriginApi.getAllOrigins().then((response) => {
                if (response.status === 200 && response.data) {
                    const mappedOrigins = response.data.map(origin => ({
                        id: origin.id,
                        name: origin.name,
                        backgroundColor: origin.backgroundColor,
                        textColor: origin.textColor,
                    }));
                    setOrigins(mappedOrigins);
                }
            }).catch((error) => {
                console.error("Error loading origins:", error);
            });
        }
    }, [open]);
    // Initialize status dropdowns when modal opens
    useEffect(() => {
        if (!open || !statusTree.length)
            return;
        // Reset status state when modal opens
        setStatusLevel1Id("");
        setStatusLevel2Id("");
        setStatusLevel3Id("");
        setLevel2Options([]);
        setLevel3Options([]);
        // Filter active statuses, and for partial access, exclude restricted statuses (blacklist)
        let filteredTree = statusTree.filter((node) => node.isActive);
        if (isPartialAccess) {
            // Partial access CANNOT work with: Signed (Getekend) or Completed (Afgerond)
            // Filter out restricted statuses (blacklist approach)
            filteredTree = filteredTree.filter((node) => !partialAccessRestrictedStatuses.includes(node.name));
        }
        setLevel1Options(filteredTree);
        // If editing an existing object, use status data already in the object (from backend)
        if (data === null || data === void 0 ? void 0 : data.id) {
            const l1 = data.statusLevel1Id || "";
            const l2 = data.statusLevel2Id || "";
            const l3 = data.statusLevel3Id || "";
            setStatusLevel1Id(l1);
            // Set original values for change detection
            setOriginalStatusLevel1Id(l1);
            setOriginalStatusLevel2Id(l2);
            setOriginalStatusLevel3Id(l3);
            if (l1) {
                const n1 = statusTree.find((x) => x.id === l1);
                // Filter only active statuses for level 2
                const l2opts = ((n1 === null || n1 === void 0 ? void 0 : n1.children) || []).filter((node) => node.isActive);
                setLevel2Options(l2opts);
                setStatusLevel2Id(l2);
                if (l2 && l2opts.length > 0) {
                    const n2 = l2opts.find((x) => x.id === l2);
                    // Filter only active statuses for level 3
                    const l3opts = ((n2 === null || n2 === void 0 ? void 0 : n2.children) || []).filter((node) => node.isActive);
                    setLevel3Options(l3opts);
                    setStatusLevel3Id(l3);
                }
            }
        }
        else {
            // New object - reset original values to empty
            setOriginalStatusLevel1Id("");
            setOriginalStatusLevel2Id("");
            setOriginalStatusLevel3Id("");
        }
    }, [open, statusTree, data === null || data === void 0 ? void 0 : data.id, data === null || data === void 0 ? void 0 : data.statusLevel1Id, data === null || data === void 0 ? void 0 : data.statusLevel2Id, data === null || data === void 0 ? void 0 : data.statusLevel3Id, isPartialAccess, partialAccessRestrictedStatuses]);
    const currentDate = new Date();
    // Get the date one month from the current date
    const oneMonthLater = new Date(currentDate);
    oneMonthLater.setMonth(oneMonthLater.getMonth() + 2);
    // Format the date if needed
    const formattedDate = `${oneMonthLater.getFullYear()}-${("0" +
        (oneMonthLater.getMonth() + 1)).slice(-2)}-${("0" + oneMonthLater.getDate()).slice(-2)}`;
    const handleAutocompleteChange = (newValue) => {
        const selectedIds = newValue === null || newValue === void 0 ? void 0 : newValue.map((newValues) => newValues.value);
        setValue("selectionListSystemsId", selectedIds);
    };
    const handleOriginChange = (newValue) => {
        const selectedIds = (newValue === null || newValue === void 0 ? void 0 : newValue.map((origin) => origin.id).filter((id) => id !== undefined)) || [];
        setValue("originIds", selectedIds);
    };
    // Debounce function to validate ObjectID
    const validateObjectID = (value) => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current); // Clear any existing timeout
        }
        debounceTimer.current = window.setTimeout(() => {
            setIsLoading(true);
            try {
                setObjectId(value.toString());
                LeadsObjectApi.checkObjectIdExists(value)
                    .then((response) => {
                    if (response && response.status === 200) {
                        if (data.objectID !== value && response.data) {
                            setError("objectID", {
                                type: "unique",
                                message: translate("Object Id already exists"),
                            });
                            setIsDisabled(true);
                        }
                        else {
                            clearErrors("objectID");
                            setIsDisabled(false);
                        }
                    }
                })
                    .finally(() => setSaving(false));
            }
            catch (error) {
                console.error("API Error:", error);
                setError("objectID", {
                    type: "api",
                    message: translate("Failed to validate ObjectID"),
                });
                setIsDisabled(true);
            }
            finally {
                setIsLoading(false);
            }
        }, 300); // Debounce delay of 300ms
    };
    return (_jsxs(CustomModal, { open: open, onClose: () => {
            // Don't allow closing while saving or checking contracts
            if (!saving && !checkingContracts) {
                onClose();
                // Reset form state after modal closes to prevent visible reset
                setTimeout(() => {
                    reset(initialFormData);
                    setMultiplicativeFactor(false);
                    setdrawingAvailable(false);
                    setSelectedImageUrl(undefined);
                    setSelectedImage(undefined);
                    setShowValidation(false);
                    // Reset status selections
                    setStatusLevel1Id("");
                    setStatusLevel2Id("");
                    setStatusLevel3Id("");
                    setLevel2Options([]);
                    setLevel3Options([]);
                    // Reset confirmation states
                    setShowContractConfirmation(false);
                    setAffectedContracts([]);
                    setPendingFormData(null);
                }, 300);
            }
        }, size: "lg", saving: saving || checkingContracts, handleSaveButtonClick: handleSaveButtonClick, heading: data.id ? translate("Edit Object") : translate("Add New Object"), saveButtonText: checkingContracts ? translate("Checking...") : translate("Save"), disabled: (isDisabled && !isEditingContracted) || checkingContracts, warningMessage: data.isContracted
            ? translate("Object is contracted, only internal comments can be edited")
            : undefined, showSaveButton: !readOnly && checkCreateEdit(RolesPermissions, Modules.Leads), children: [_jsx(Box, { sx: {
                    p: { xs: "6px 12px", md: "24px 48px" }
                }, children: _jsxs("form", { onSubmit: (event) => {
                        event.preventDefault();
                        if (isEditingContracted) {
                            const currentValues = getValues();
                            onSubmit(currentValues);
                        }
                        else {
                            // Delegate to RHF to run validations
                            const submit = handleSubmit(onSubmit);
                            submit(event);
                        }
                    }, children: [_jsx("input", Object.assign({ type: "hidden" }, register("id"))), _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: {
                                        xs: 12,
                                        md: 12
                                    }, children: _jsx(Controller, { name: "name", control: control, rules: {
                                            required: {
                                                value: true,
                                                message: translate("NameRequired"),
                                            },
                                        }, render: ({ field }) => {
                                            var _a;
                                            return (_jsxs(FormControl, { fullWidth: true, variant: "standard", size: "medium", children: [_jsx(TextField, Object.assign({ label: "Naam", variant: "outlined" }, field, { fullWidth: true })), _jsx(FormHelperText, { error: true, children: (_a = errors.name) === null || _a === void 0 ? void 0 : _a.message })] }));
                                        } }) }), _jsx(Grid, { size: {
                                        xs: 12,
                                        md: 6
                                    }, children: _jsx(Controller, { name: "location", control: control, rules: {
                                            validate: (value) => {
                                                const latitude = getValues("latitude");
                                                const longitude = getValues("longitude");
                                                if (!value && (!latitude || !longitude)) {
                                                    return translate("Either Location or Coordinates are required");
                                                }
                                                return true;
                                            },
                                        }, render: ({ field: { onChange, value } }) => {
                                            var _a;
                                            return (_jsxs(FormControl, { fullWidth: true, variant: "outlined", size: "small", children: [_jsx(Autocomplete, { isOptionEqualToValue: (option, value) => option.id === value.id, disablePortal: true, fullWidth: true, getOptionLabel: (option) => option.weergavenaam, options: locations, disabled: isLoading, noOptionsText: translate("noOptions"), loading: isFetching, onChange: (_, value) => {
                                                            onLocationChange(value);
                                                            onChange(value);
                                                        }, value: value, renderInput: (params) => (_jsx(TextField, Object.assign({ placeholder: "Zoek locatie", error: !!errors.location }, params, { label: "Locatie", onChange: (e) => {
                                                                fetchLocations(e.target.value);
                                                                resetLocation();
                                                            }, slotProps: Object.assign(Object.assign({}, params.slotProps), { input: Object.assign(Object.assign({}, params.slotProps.input), { endAdornment: (_jsxs(Fragment, { children: [isFetching ? (_jsx(CircularProgress, { color: "inherit", size: 20 })) : null, params.slotProps.input.endAdornment] })) }) }) }))) }), _jsx(FormHelperText, { error: true, children: (_a = errors.location) === null || _a === void 0 ? void 0 : _a.message })] }));
                                        } }) }), _jsx(Grid, { size: {
                                        xs: 12,
                                        md: 6
                                    }, children: _jsxs(Box, { sx: { display: "flex", gap: 2, alignItems: "flex-start" }, children: [_jsx(Controller, { name: "latitude", control: control, rules: {
                                                    validate: (value) => {
                                                        const location = getValues("location");
                                                        const longitude = getValues("longitude");
                                                        if (!location && (!value || !longitude)) {
                                                            return translate("Either Location or Coordinates are required");
                                                        }
                                                        else if (value &&
                                                            longitude &&
                                                            !validateCoordinates(longitude, value)) {
                                                            return translate("InvalidCoordinates");
                                                        }
                                                        return true;
                                                    },
                                                }, render: ({ field }) => {
                                                    var _a;
                                                    return (_jsxs(FormControl, { fullWidth: true, variant: "standard", size: "medium", children: [_jsx(TextField, Object.assign({ label: "Latitude", variant: "outlined" }, field, { fullWidth: true, onChange: (e) => {
                                                                    field.onChange(e);
                                                                    const lat = e.target.value;
                                                                    const lon = getValues("longitude");
                                                                    const address = getValues("locationId");
                                                                    if (!address && lat && lon) {
                                                                        fetchImage(lon, lat);
                                                                    }
                                                                }, slotProps: {
                                                                    inputLabel: { shrink: Boolean(field.value) }
                                                                } })), _jsx(FormHelperText, { error: true, children: (_a = errors.latitude) === null || _a === void 0 ? void 0 : _a.message })] }));
                                                } }), _jsx(Controller, { name: "longitude", control: control, rules: {
                                                    validate: (value) => {
                                                        const location = getValues("location");
                                                        const latitude = getValues("latitude");
                                                        if (!location && (!value || !latitude)) {
                                                            return translate("Either Location or Coordinates are required");
                                                        }
                                                        else if (value &&
                                                            latitude &&
                                                            !validateCoordinates(value, latitude)) {
                                                            return translate("InvalidCoordinates");
                                                        }
                                                        return true;
                                                    },
                                                }, render: ({ field }) => {
                                                    var _a;
                                                    return (_jsxs(FormControl, { fullWidth: true, variant: "standard", size: "medium", children: [_jsx(TextField, Object.assign({ label: "Longitude", variant: "outlined" }, field, { fullWidth: true, onChange: (e) => {
                                                                    field.onChange(e);
                                                                    const lon = e.target.value;
                                                                    const lat = getValues("latitude");
                                                                    const address = getValues("locationId");
                                                                    if (!address && lat && lon) {
                                                                        fetchImage(lon, lat);
                                                                    }
                                                                }, slotProps: {
                                                                    inputLabel: { shrink: Boolean(field.value) }
                                                                } })), _jsx(FormHelperText, { error: true, children: (_a = errors.longitude) === null || _a === void 0 ? void 0 : _a.message })] }));
                                                } })] }) }), _jsx(Grid, { size: {
                                        xs: 6,
                                        md: 4
                                    }, children: _jsx(Controller, { name: "companyRoleId", control: control, rules: {
                                            required: {
                                                value: true,
                                                message: translate("Company Role is required"),
                                            },
                                        }, render: ({ field: { onChange, value } }) => {
                                            var _a;
                                            return (_jsxs(FormControl, { fullWidth: true, children: [_jsx(Autocomplete, { disablePortal: true, fullWidth: true, id: "company-role-autocomplete", options: companyRoles, isOptionEqualToValue: (option, v) => option.id === v.id, getOptionLabel: (option) => option.name || "", value: companyRoles.find((r) => r.id === value) || null, onChange: (_, selected) => {
                                                            var _a;
                                                            onChange((_a = selected === null || selected === void 0 ? void 0 : selected.id) !== null && _a !== void 0 ? _a : "");
                                                        }, noOptionsText: translate("noOptions"), renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { fullWidth: true, label: translate("Role") }))) }), _jsx(FormHelperText, { error: true, children: (_a = errors.companyRoleId) === null || _a === void 0 ? void 0 : _a.message })] }));
                                        } }) }), _jsx(Grid, { size: {
                                        xs: 6,
                                        md: 2
                                    }, children: _jsx(Controller, { name: "projectNumber", control: control, render: ({ field }) => {
                                            var _a;
                                            return (_jsxs(FormControl, { fullWidth: true, variant: "outlined", children: [_jsx(InputLabel, { className: "border-radius", htmlFor: "area", children: translate("ProjectNumber") }), _jsx(OutlinedInput, Object.assign({ className: "border-radius", id: "projectNumber" }, field, { label: translate("ProjectNumber"), sx: { borderRadius: "8px" }, error: Boolean(errors.projectNumber) })), _jsx(FormHelperText, { error: true, children: (_a = errors.projectNumber) === null || _a === void 0 ? void 0 : _a.message })] }));
                                        } }) }), _jsx(Grid, { size: {
                                        xs: 6,
                                        md: 2
                                    }, children: _jsx(Controller, { name: "objectID", control: control, rules: {
                                            required: {
                                                value: true,
                                                message: translate("Object Id is required"),
                                            },
                                            validate: {
                                                positiveNumber: (value) => {
                                                    const numberValue = Number(value);
                                                    return (numberValue > 0 ||
                                                        translate("Please enter a positive number"));
                                                },
                                            },
                                        }, render: ({ field }) => {
                                            var _a;
                                            return (_jsxs(FormControl, { fullWidth: true, variant: "outlined", children: [_jsx(InputLabel, { className: "border-radius", htmlFor: "area", children: translate("ObjectId") }), _jsx(OutlinedInput, Object.assign({ className: "border-radius", id: "objectId" }, field, { onChange: (e) => {
                                                            field.onChange(e);
                                                            validateObjectID(Number(e.target.value)); // Trigger debounce validation
                                                        }, label: translate("ObjectId"), sx: { borderRadius: "8px" }, type: "number", error: Boolean(errors.objectID), inputProps: {
                                                            step: "any", // Allow decimal values
                                                        } })), _jsx(FormHelperText, { error: true, children: (_a = errors.objectID) === null || _a === void 0 ? void 0 : _a.message })] }));
                                        } }) }), _jsx(Grid, { size: {
                                        xs: 12,
                                        md: 2
                                    }, children: _jsx(Controller, { name: "area", control: control, rules: {
                                            required: {
                                                value: true,
                                                message: translate("Area of roof surfaces is required"),
                                            },
                                            validate: {
                                                positiveNumber: (value) => {
                                                    return (value >= 0 ||
                                                        translate("Please enter a positive number"));
                                                },
                                            },
                                        }, render: ({ field }) => {
                                            var _a;
                                            return (_jsxs(FormControl, { fullWidth: true, variant: "outlined", children: [_jsx(InputLabel, { className: "border-radius", htmlFor: "area", children: translate("Total Roof Area") }), _jsx(OutlinedInput, Object.assign({ className: "border-radius", id: "totalRoofSurfaces" }, field, { label: translate("Total Roof Area"), sx: { borderRadius: "8px" }, type: "number", error: Boolean(errors.area), inputProps: {
                                                            step: "any", // Allow decimal values
                                                        } })), _jsx(FormHelperText, { error: true, children: (_a = errors.area) === null || _a === void 0 ? void 0 : _a.message })] }));
                                        } }) }), _jsx(Grid, { size: {
                                        xs: 12,
                                        md: 2
                                    }, children: _jsx(Controller, { name: "totalRoofSurfaces", control: control, rules: {
                                            required: {
                                                value: true,
                                                message: translate("Total roof surfaces is required"),
                                            },
                                            validate: {
                                                positiveNumber: (value) => {
                                                    return (value >= 0 ||
                                                        translate("Please enter a positive number"));
                                                },
                                            },
                                        }, render: ({ field }) => {
                                            var _a;
                                            return (_jsxs(FormControl, { fullWidth: true, variant: "outlined", children: [_jsx(InputLabel, { className: "border-radius", htmlFor: "totalRoofSurfaces", children: translate("Roof surfaces") }), _jsx(OutlinedInput, Object.assign({ className: "border-radius", id: "area" }, field, { label: translate("Roof surfaces"), type: "number", sx: { borderRadius: "8px" }, error: Boolean(errors.totalRoofSurfaces), inputProps: {
                                                            step: "any", // Allow decimal values
                                                        } })), _jsx(FormHelperText, { error: true, children: (_a = errors.totalRoofSurfaces) === null || _a === void 0 ? void 0 : _a.message })] }));
                                        } }) }), ((_d = data.otherCompanyRoles) !== null && _d !== void 0 ? _d : []).length > 0 && (_jsxs(Grid, { size: 12, children: [_jsx(Typography, { variant: "body1", sx: {
                                                color: "text.primary"
                                            }, children: translate("Linked Leads") }), _jsx(Box, { sx: { display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }, children: ((_e = data.otherCompanyRoles) !== null && _e !== void 0 ? _e : [])
                                                .slice()
                                                .sort((a, b) => {
                                                var _a, _b;
                                                const aName = (_a = a === null || a === void 0 ? void 0 : a.companyRoleName) !== null && _a !== void 0 ? _a : "";
                                                const bName = (_b = b === null || b === void 0 ? void 0 : b.companyRoleName) !== null && _b !== void 0 ? _b : "";
                                                return aName.localeCompare(bName, undefined, { sensitivity: "base" });
                                            })
                                                .map((item, index) => {
                                                var _a, _b, _c, _d, _e, _f;
                                                const chipLabel = `${(_a = item === null || item === void 0 ? void 0 : item.companyRoleName) !== null && _a !== void 0 ? _a : ""} - ${String((_b = item === null || item === void 0 ? void 0 : item.leadNumber) !== null && _b !== void 0 ? _b : "")} - ${(_c = item === null || item === void 0 ? void 0 : item.companyName) !== null && _c !== void 0 ? _c : ""}`;
                                                return (_jsx(Chip, { label: chipLabel, clickable: true, component: "a", href: `/lead-detail?id=${item === null || item === void 0 ? void 0 : item.leadId}&companyId=${item === null || item === void 0 ? void 0 : item.companyId}&orderNumber=${String((_d = item === null || item === void 0 ? void 0 : item.leadNumber) !== null && _d !== void 0 ? _d : "")}`, target: "_blank", rel: "noopener", sx: {
                                                        minWidth: "fit-content",
                                                        whiteSpace: "nowrap",
                                                        background: "#F4EFFB",
                                                        cursor: "pointer",
                                                        textDecoration: "none",
                                                        "& .MuiChip-label": {
                                                            textDecoration: "underline",
                                                            color: "#1976d2",
                                                            fontWeight: 500,
                                                        },
                                                        "&:hover": {
                                                            backgroundColor: "#F4EFFB",
                                                        },
                                                    } }, `${(_e = item === null || item === void 0 ? void 0 : item.leadId) !== null && _e !== void 0 ? _e : index}-${(_f = item === null || item === void 0 ? void 0 : item.companyRoleId) !== null && _f !== void 0 ? _f : index}`));
                                            }) })] })), _jsx(Grid, { size: {
                                        xs: 6,
                                        md: 3
                                    }, children: _jsx(Controller, { name: "installationDate", control: control, render: ({ field }) => {
                                            var _a;
                                            return (_jsxs(FormControl, { fullWidth: true, variant: "outlined", children: [_jsx(LocalizationProvider, { dateAdapter: AdapterDayjs, children: _jsx(DatePicker, { value: field.value ? dayjs(field.value) : null, views: ["day", "month", "year"], label: translate("Installation Date"), slots: {
                                                                openPickerIcon: Calendar,
                                                            }, onChange: (date) => {
                                                                const formattedDate = date && dayjs(date).format("YYYY-MM-DD");
                                                                field.onChange(formattedDate);
                                                            }, sx: { width: "100%" }, format: "DD,MMM, YYYY" }) }), _jsx(FormHelperText, { error: true, children: (_a = errors.installationDate) === null || _a === void 0 ? void 0 : _a.message })] }));
                                        } }) }), _jsx(Grid, { size: {
                                        xs: 6,
                                        md: 3
                                    }, children: _jsx(Box, { sx: {
                                            display: "flex",
                                            height: "100%",
                                            flexDirection: { xs: "column", md: "row" },
                                            gap: 2,
                                            alignItems: { xs: "flex-start", md: "center" }
                                        }, children: _jsx(Controller, { name: "price", rules: {
                                                required: {
                                                    value: true,
                                                    message: translate("Price is required"),
                                                },
                                                validate: {
                                                    positiveNumber: (value) => Number(value) >= 0 ||
                                                        translate("Please enter a positive number"),
                                                },
                                            }, control: control, render: ({ field }) => {
                                                var _a;
                                                return (_jsxs(FormControl, { fullWidth: true, variant: "outlined", size: "medium", children: [_jsx(InputLabel, { className: "border-radius", htmlFor: "price", children: translate("Price") }), _jsx(OutlinedInput, Object.assign({ className: "border-radius", id: "price" }, field, { label: translate("Price"), sx: { borderRadius: "8px" }, type: "number", error: Boolean(errors.price), inputProps: {
                                                                step: "any", // Allow decimal values
                                                            } })), _jsx(FormHelperText, { error: true, children: (_a = errors.price) === null || _a === void 0 ? void 0 : _a.message })] }));
                                            } }) }) }), _jsx(Grid, { size: 6, children: _jsx(Controller, { name: "origins", control: control, rules: {
                                            required: {
                                                value: !isEditingContracted,
                                                message: translate("AtLeastOneOriginRequired"),
                                            },
                                            validate: {
                                                atLeastOne: (value) => {
                                                    if (isEditingContracted)
                                                        return true;
                                                    return ((value && value.length > 0) ||
                                                        translate("AtLeastOneOriginRequired"));
                                                },
                                            },
                                        }, render: ({ field: { onChange, value } }) => {
                                            var _a;
                                            return (_jsxs(FormControl, { fullWidth: true, variant: "outlined", size: "small", error: !!errors.origins, children: [_jsx(Autocomplete, { multiple: true, disablePortal: true, limitTags: 3, id: "origins-combo-box", options: origins, getOptionLabel: (option) => option.name, noOptionsText: translate("noOptions"), value: value || [], onChange: (_, values) => {
                                                            onChange(values);
                                                            handleOriginChange(values);
                                                        }, isOptionEqualToValue: (option, value) => option.id === value.id, renderValue: (tagValue, getItemProps) => tagValue.map((option, index) => (_jsx(Chip, Object.assign({ label: option.name }, getItemProps({ index }), { style: {
                                                                backgroundColor: option.backgroundColor,
                                                                color: option.textColor,
                                                            } })))), renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: translate("Origins"), error: !!errors.origins }))) }), _jsx(FormHelperText, { error: true, children: (_a = errors.origins) === null || _a === void 0 ? void 0 : _a.message })] }));
                                        } }) }), _jsx(Grid, { size: 12, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: {
                                                    xs: 12,
                                                    md: 6
                                                }, children: _jsx(Controller, { name: "roofAccess", control: control, rules: {
                                                        required: {
                                                            value: true,
                                                            message: translate("Roof access is required"),
                                                        },
                                                    }, render: ({ field }) => {
                                                        var _a;
                                                        return (_jsxs(FormControl, { fullWidth: true, children: [_jsx(InputLabel, { id: "demo-simple-select-label", children: translate("How can we get to the roof?") }), _jsxs(Select, Object.assign({ labelId: "demo-simple-select-label", id: "demo-simple-select" }, field, { fullWidth: true, label: translate("How can we get to the roof?"), children: [_jsx(MenuItem, { value: 1, children: _jsx(Typography, { variant: "body1", sx: {
                                                                                    color: "var(--n-primary, #130922)"
                                                                                }, children: "Mobiele Ladder < 6m" }) }), _jsx(MenuItem, { value: 2, children: _jsx(Typography, { variant: "body1", sx: {
                                                                                    color: "var(--n-primary, #130922)"
                                                                                }, children: "Mobiele Ladder > 6m" }) }), _jsx(MenuItem, { value: 3, children: _jsx(Typography, { variant: "body1", sx: {
                                                                                    color: "var(--n-primary, #130922)"
                                                                                }, children: "Kooi of gevel ladder" }) }), _jsx(MenuItem, { value: 4, children: _jsx(Typography, { variant: "body1", sx: {
                                                                                    color: "var(--n-primary, #130922)"
                                                                                }, children: "Dakluik" }) }), _jsx(MenuItem, { value: 5, children: _jsx(Typography, { variant: "body1", sx: {
                                                                                    color: "var(--n-primary, #130922)"
                                                                                }, children: "Deur" }) }), _jsx(MenuItem, { value: 6, children: _jsx(Typography, { variant: "body1", sx: {
                                                                                    color: "var(--n-primary, #130922)"
                                                                                }, children: "Raam uitstap" }) }), _jsx(MenuItem, { value: 7, children: _jsx(Typography, { variant: "body1", sx: {
                                                                                    color: "var(--n-primary, #130922)"
                                                                                }, children: "Anders" }) }), _jsx(MenuItem, { value: 8, children: _jsx(Typography, { variant: "body1", sx: {
                                                                                    color: "var(--n-primary, #130922)"
                                                                                }, children: "Hoogwerker" }) }), _jsx(MenuItem, { value: 9, children: _jsx(Typography, { variant: "body1", sx: {
                                                                                    color: "var(--n-primary, #130922)"
                                                                                }, children: "Onbekend" }) })] })), _jsx(FormHelperText, { error: true, children: (_a = errors.roofAccess) === null || _a === void 0 ? void 0 : _a.message })] }));
                                                    } }) }), _jsx(Grid, { size: {
                                                    xs: 12,
                                                    md: 6
                                                }, children: data.referralObjectNumber != null ? (_jsxs(_Fragment, { children: [(syncedDrawingUrls.length > 0 || syncedDrawingNames.length > 0) && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body2", sx: {
                                                                        color: "var(--Neutral-600, #6C757D)",
                                                                        mb: 1
                                                                    }, children: translate("Drawing Link") }), _jsx(Box, { sx: { display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }, children: Array.from({ length: Math.max(syncedDrawingUrls.length, syncedDrawingNames.length) }).map((_, idx) => {
                                                                        var _a, _b;
                                                                        const url = syncedDrawingUrls[idx];
                                                                        // Use stored name if available, else parse from URL
                                                                        const storedName = syncedDrawingNames[idx];
                                                                        const fallbackName = ((_b = (_a = url === null || url === void 0 ? void 0 : url.split('/').pop()) === null || _a === void 0 ? void 0 : _a.split('?')[0]) === null || _b === void 0 ? void 0 : _b.replace(/^[0-9a-f-]{36}_/i, '')) || `Tekening ${idx + 1}`;
                                                                        const label = storedName || fallbackName;
                                                                        return (_jsx(Chip, { label: label, clickable: !!url, onClick: url ? () => window.open(url, '_blank') : undefined, onDelete: () => {
                                                                                setSyncedDrawingUrls((prev) => prev.filter((_, i) => i !== idx));
                                                                                setSyncedDrawingNames((prev) => prev.filter((_, i) => i !== idx));
                                                                            }, sx: { maxWidth: 240 } }, idx));
                                                                    }) })] })), _jsx(TextField, { label: translate("Additional drawing link"), value: additionalDrawingLink, onChange: (e) => setAdditionalDrawingLink(e.target.value), placeholder: "https://...", fullWidth: true, variant: "outlined" })] })) : (_jsx(Controller, { name: "drawingLinkNames", control: control, render: ({ field }) => (_jsx(FormControl, { fullWidth: true, variant: "standard", size: "medium", children: _jsx(TextField, Object.assign({ label: "Tekening link", variant: "outlined", placeholder: "Tekening link" }, field, { value: field.value || '', error: Boolean(errors.drawingLinkNames), fullWidth: true })) })) })) }), _jsx(Grid, { size: {
                                                    xs: 12,
                                                    md: 6
                                                }, children: _jsxs(Box, { className: "image-box theme", sx: {
                                                        position: "relative",
                                                        border: "1px solid var(--Neutral-200, #E9ECEF)",
                                                        borderRadius: 1,
                                                        overflow: "hidden",
                                                        height: "100%",
                                                        minHeight: "190px",
                                                    }, children: [selectedImageUrl || data.imageUrl ? (_jsx(Box, { className: "image-container", children: _jsx(ZoomableImage, { src: selectedImageUrl ? selectedImageUrl : data.imageUrl }) })) : (_jsxs(Box, { sx: {
                                                                minHeight: "190px",
                                                                height: "100%",
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                gap: 1,
                                                                color: "var(--Neutral-500, #ADB5BD)",
                                                            }, children: [_jsx(ImageSquare, { size: 40 }), _jsx(Typography, { variant: "body2", children: translate("Nog geen dak ingeladen") })] })), loading && (_jsx(Box, { sx: {
                                                                position: "absolute",
                                                                inset: 0,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                bgcolor: "rgba(255, 255, 255, 0.6)",
                                                            }, children: _jsx(CircularProgress, { size: 28 }) }))] }) }), _jsx(Grid, { size: {
                                                    xs: 12,
                                                    md: 6
                                                }, children: _jsx(Controller, { name: "selectionListSystems", control: control, render: ({ field: { onChange } }) => {
                                                        var _a, _b;
                                                        return (_jsxs(FormControl, { fullWidth: true, variant: "outlined", size: "small", error: !!errors.selectionListSystems, sx: {
                                                                border: "1px solid var(--Neutral-200, #E9ECEF)",
                                                                borderRadius: 1,
                                                                p: 1.5,
                                                                height: "100%",
                                                                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                                                            }, children: [_jsx(FormLabel, { sx: {
                                                                        fontSize: 12,
                                                                        color: "var(--Neutral-600, #6C757D)",
                                                                        mb: 0.5,
                                                                    }, children: translate("Selecteerd assets") }), _jsx(Autocomplete, { multiple: true, disablePortal: true, id: "combo-box", options: assetsData, getOptionLabel: (option) => option.text, noOptionsText: translate("noOptions"), defaultValue: (_a = (data.selectionListSystemsId &&
                                                                        assetsData
                                                                            .filter((option) => data.selectionListSystemsId.some((id) => id === option.value))
                                                                            .sort((a, b) => a.text.localeCompare(b.text)))) !== null && _a !== void 0 ? _a : undefined, onChange: (_, values) => {
                                                                        onChange(values);
                                                                        handleAutocompleteChange(values);
                                                                    }, renderValue: (tagValue, getItemProps) => {
                                                                        const visibleCount = assetsPanelFocused
                                                                            ? tagValue.length
                                                                            : Math.min(tagValue.length, ASSETS_COLLAPSED_COUNT);
                                                                        const hiddenCount = tagValue.length - visibleCount;
                                                                        return (_jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.5, width: "100%", mb: 0.5 }, children: tagValue.slice(0, visibleCount).map((option, index) => {
                                                                                const { key, onDelete } = getItemProps({ index });
                                                                                const isLastVisible = index === visibleCount - 1;
                                                                                return (_jsxs(Box, { sx: {
                                                                                        display: "flex",
                                                                                        alignItems: "center",
                                                                                        justifyContent: "space-between",
                                                                                        bgcolor: "var(--Neutral-100, #F1F3F5)",
                                                                                        borderRadius: 1,
                                                                                        px: 1,
                                                                                        py: 0.5,
                                                                                    }, children: [_jsx(Typography, { variant: "body2", children: option.text }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.5 }, children: [isLastVisible && hiddenCount > 0 && (_jsxs(Typography, { variant: "caption", sx: {
                                                                                                        color: "text.secondary"
                                                                                                    }, children: ["+", hiddenCount] })), _jsx(IconButton, { size: "small", onClick: onDelete, children: _jsx(X, { size: 14 }) })] })] }, key));
                                                                            }) }));
                                                                    }, renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { variant: "standard", placeholder: translate("Toevoegen..."), inputRef: selectionInputRef, onFocus: (e) => {
                                                                            var _a, _b;
                                                                            setAssetsPanelFocused(true);
                                                                            (_b = (_a = params.slotProps.htmlInput) === null || _a === void 0 ? void 0 : _a.onFocus) === null || _b === void 0 ? void 0 : _b.call(_a, e);
                                                                        }, onBlur: (e) => {
                                                                            var _a, _b;
                                                                            setAssetsPanelFocused(false);
                                                                            (_b = (_a = params.slotProps.htmlInput) === null || _a === void 0 ? void 0 : _a.onBlur) === null || _b === void 0 ? void 0 : _b.call(_a, e);
                                                                        } }))) }), _jsx(FormHelperText, { children: (_b = errors.selectionListSystems) === null || _b === void 0 ? void 0 : _b.message })] }));
                                                    } }) })] }) }), _jsx(Grid, { size: 12, children: _jsx(Controller, { name: "internalComments", control: control, render: ({ field }) => {
                                            var _a;
                                            return (_jsxs(FormControl, { fullWidth: true, variant: "standard", size: "medium", children: [_jsxs(Box, { sx: { position: "relative" }, children: [_jsx(TextField, Object.assign({ label: translate("InternalComments"), placeholder: translate("Voer interne opmerkingen in"), variant: "outlined", margin: "normal" }, field, { inputRef: internalCommentsRef, multiline: true, fullWidth: true, rows: 6, sx: { "& .MuiOutlinedInput-root": { paddingRight: "40px" } } })), _jsx(Tooltip, { title: "Voeg tijdstempel toe (F10)", children: _jsx(IconButton, { size: "small", "aria-label": "Voeg tijdstempel toe", onClick: insertTimestamp, sx: { position: "absolute", top: 20, right: 4 }, children: _jsx(ClockCountdown, { size: 20 }) }) })] }), _jsx(FormHelperText, { error: true, children: (_a = errors.internalComments) === null || _a === void 0 ? void 0 : _a.message })] }));
                                        } }) }), _jsx(Grid, { size: 12, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: {
                                                    xs: 12,
                                                    md: 6
                                                }, children: _jsx(Controller, { name: "context", control: control, render: ({ field }) => {
                                                        var _a;
                                                        return (_jsxs(FormControl, { fullWidth: true, variant: "standard", size: "medium", children: [_jsx(TextField, Object.assign({ label: translate("Context"), placeholder: translate("Instructions on how we can register"), variant: "outlined", margin: "normal" }, field, { multiline: true, fullWidth: true, rows: 3 })), _jsx(FormHelperText, { error: true, children: (_a = errors.context) === null || _a === void 0 ? void 0 : _a.message })] }));
                                                    } }) }), _jsx(Grid, { size: {
                                                    xs: 12,
                                                    md: 6
                                                }, children: _jsx(Controller, { name: "externalComments", control: control, render: ({ field }) => {
                                                        var _a;
                                                        return (_jsxs(FormControl, { fullWidth: true, variant: "standard", size: "medium", children: [_jsx(TextField, Object.assign({ label: translate("ExternalComments"), placeholder: translate("Voer externe opmerkingen in"), variant: "outlined", margin: "normal" }, field, { multiline: true, fullWidth: true, rows: 3 })), _jsx(FormHelperText, { error: true, children: (_a = errors.externalComments) === null || _a === void 0 ? void 0 : _a.message })] }));
                                                    } }) })] }) }), _jsx(Grid, { size: 12, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { container: true, size: {
                                                    xs: 12,
                                                    md: 6
                                                }, children: _jsxs(FormControl, { children: [_jsxs(FormLabel, { id: "demo-controlled-radio-buttons-group", children: ["Moet er een tekening gemaakt worden?", _jsx(Tooltip, { title: " extra kosten 60 euro.", children: _jsx(IconButton, { children: _jsx(Info, { size: 24 }) }) })] }), _jsxs(RadioGroup, { "aria-labelledby": "demo-controlled-radio-buttons-group", name: "controlled-radio-buttons-group", value: drawingAvailable.toString(), onChange: handleDrawingRadioChange, children: [_jsx(FormControlLabel, { value: "true", control: _jsx(Radio, {}), label: "Ja" }), _jsx(FormControlLabel, { value: "false", control: _jsx(Radio, {}), label: "Nee" })] })] }) }), _jsxs(Grid, { size: {
                                                    xs: 12,
                                                    md: 6
                                                }, children: [_jsx(Controller, { name: "preferredMonth", control: control, rules: {
                                                            required: {
                                                                value: !userPreferredMonth,
                                                                message: "Vereist.",
                                                            },
                                                        }, render: ({ field }) => {
                                                            var _a;
                                                            return (_jsxs(FormControl, { fullWidth: true, variant: "outlined", children: [_jsx(LocalizationProvider, { dateAdapter: AdapterDayjs, children: _jsx(DatePicker, { value: userPreferredMonth
                                                                                ? null
                                                                                : field.value && dayjs(field.value), views: ["month", "year"], label: translate("Preferred Month"), slots: {
                                                                                openPickerIcon: Calendar,
                                                                            }, onChange: (date) => {
                                                                                const formattedDate = date && dayjs(date).format("YYYY-MM-DD");
                                                                                field.onChange(formattedDate);
                                                                            }, sx: { width: "100%" }, format: "MMM, YYYY", minDate: dayjs(formattedDate), disabled: userPreferredMonth === true }) }), _jsx(FormHelperText, { error: true, children: (_a = errors.preferredMonth) === null || _a === void 0 ? void 0 : _a.message })] }));
                                                        } }), _jsxs(Box, { sx: {
                                                            display: "flex",
                                                            paddingTop: "5px",
                                                            paddingBottom: "10px"
                                                        }, children: [_jsx(FormControl, { variant: "outlined", children: _jsx(Checkbox, { className: "border-radius", id: "userPreferredMonth", sx: { padding: "0 !important" }, checked: userPreferredMonth, onChange: (e) => {
                                                                        handleUserPreferredCheckBoxChange(e);
                                                                    } }) }), _jsx(Typography, { children: " Vrij te plannen" }), _jsx(Tooltip, { title: "5% korting", children: _jsx(IconButton, { sx: { padding: "0", paddingLeft: "2px" }, children: _jsx(Info, { size: 24 }) }) })] })] })] }) }), _jsx("input", Object.assign({ type: "hidden" }, register("openingStartHoursString"))), _jsx("input", Object.assign({ type: "hidden" }, register("openingEndHoursString"))), _jsx("input", Object.assign({ type: "hidden" }, register("registrationTime"))), data.id && !shouldHideStatusDropdowns && (_jsxs(_Fragment, { children: [_jsx(Grid, { size: {
                                                xs: 12,
                                                md: 4
                                            }, children: _jsx(FormControl, { fullWidth: true, variant: "outlined", size: "small", children: _jsx(Autocomplete, { disablePortal: true, fullWidth: true, disabled: isStatusDropdownDisabled, options: level1Options, isOptionEqualToValue: (o, v) => o.id === v.id, getOptionLabel: (o) => o.name, value: level1Options.find((o) => o.id === statusLevel1Id) || null, onChange: (_, v) => {
                                                        const id = (v === null || v === void 0 ? void 0 : v.id) || "";
                                                        setStatusLevel1Id(id);
                                                        const n1 = statusTree.find((x) => x.id === id);
                                                        // Filter only active statuses for level 2
                                                        let level2Opts = ((n1 === null || n1 === void 0 ? void 0 : n1.children) || []).filter((node) => node.isActive);
                                                        // For partial access, exclude restricted statuses (blacklist approach)
                                                        if (isPartialAccess) {
                                                            level2Opts = level2Opts.filter((node) => !partialAccessRestrictedStatuses.includes(node.name));
                                                        }
                                                        setLevel2Options(level2Opts);
                                                        setStatusLevel2Id("");
                                                        setStatusLevel3Id("");
                                                        setLevel3Options([]);
                                                    }, renderInput: (params) => _jsx(TextField, Object.assign({}, params, { label: translate("Status") })) }) }) }), _jsx(Grid, { size: {
                                                xs: 12,
                                                md: 4
                                            }, children: _jsx(FormControl, { fullWidth: true, variant: "outlined", size: "small", children: _jsx(Autocomplete, { disablePortal: true, fullWidth: true, disabled: isStatusDropdownDisabled, options: level2Options, isOptionEqualToValue: (o, v) => o.id === v.id, getOptionLabel: (o) => o.name, value: level2Options.find((o) => o.id === statusLevel2Id) || null, onChange: (_, v) => {
                                                        const id = (v === null || v === void 0 ? void 0 : v.id) || "";
                                                        setStatusLevel2Id(id);
                                                        const n2 = level2Options.find((x) => x.id === id);
                                                        // Filter only active statuses for level 3
                                                        let level3Opts = ((n2 === null || n2 === void 0 ? void 0 : n2.children) || []).filter((node) => node.isActive);
                                                        // For partial access, exclude restricted statuses (blacklist approach)
                                                        if (isPartialAccess) {
                                                            level3Opts = level3Opts.filter((node) => !partialAccessRestrictedStatuses.includes(node.name));
                                                        }
                                                        setLevel3Options(level3Opts);
                                                        setStatusLevel3Id("");
                                                    }, renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: translate("Reason"), error: showValidation && !!statusLevel1Id && level2Options.length > 0 && !statusLevel2Id, helperText: showValidation && !!statusLevel1Id && level2Options.length > 0 && !statusLevel2Id ? translate("fieldRequired") : "" }))) }) }) }), _jsx(Grid, { size: {
                                                xs: 12,
                                                md: 4
                                            }, children: _jsx(FormControl, { fullWidth: true, variant: "outlined", size: "small", children: _jsx(Autocomplete, { disablePortal: true, fullWidth: true, disabled: isStatusDropdownDisabled, options: level3Options, isOptionEqualToValue: (o, v) => o.id === v.id, getOptionLabel: (o) => o.name, value: level3Options.find((o) => o.id === statusLevel3Id) || null, onChange: (_, v) => setStatusLevel3Id((v === null || v === void 0 ? void 0 : v.id) || ""), renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: translate("Keuzemenu (dropdown)"), error: showValidation && !!statusLevel2Id && level3Options.length > 0 && !statusLevel3Id, helperText: showValidation && !!statusLevel2Id && level3Options.length > 0 && !statusLevel3Id ? translate("fieldRequired") : "" }))) }) }) })] }))] }), _jsx("button", { ref: hiddenButtonRef, type: "submit", hidden: true, children: "Submit" })] }) }), _jsx(ContractAffectedConfirmationDialog, { open: showContractConfirmation, onClose: handleCancelStatusChange, onConfirm: handleConfirmStatusChange, affectedContracts: affectedContracts, loading: saving })] }));
};
export default ObjectModal;
